import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import {
  getPeriod,
  shiftPeriod,
  StatisticsPeriod,
  StatisticsPeriodType,
} from '../../utils/statistics';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';

interface PeriodFilterProps {
  period: StatisticsPeriod;
  onChange: (period: StatisticsPeriod) => void;
}

const PERIOD_TYPES: { type: StatisticsPeriodType; labelKey: string }[] = [
  { type: 'week', labelKey: COMMON_TRANSLATE_KEYS.STATS_WEEK },
  { type: 'month', labelKey: COMMON_TRANSLATE_KEYS.STATS_MONTH },
  { type: 'year', labelKey: COMMON_TRANSLATE_KEYS.STATS_YEAR },
  { type: 'custom', labelKey: COMMON_TRANSLATE_KEYS.STATS_CUSTOM },
];

const INPUT_FORMAT = 'YYYY-MM-DD';

const PeriodFilter = ({ period, onChange }: PeriodFilterProps) => {
  const { t, i18n } = useTranslation();

  const handleTypeChange = (type: StatisticsPeriodType) => {
    if (type === period.type) {
      return;
    }

    if (type === 'custom') {
      onChange({ ...period, type });
      return;
    }

    onChange(getPeriod(type, new Date()));
  };

  const handleCustomDateChange = (edge: 'start' | 'end', value: string) => {
    const date = dayjs(value);

    if (!value || !date.isValid()) {
      return;
    }

    const start = edge === 'start' ? date.startOf('day') : dayjs(period.start);
    const end = edge === 'end' ? date.endOf('day') : dayjs(period.end);

    if (start.isAfter(end)) {
      return;
    }

    onChange({ type: 'custom', start: start.toDate(), end: end.toDate() });
  };

  const getPeriodLabel = () => {
    const start = dayjs(period.start).locale(i18n.language);
    const end = dayjs(period.end).locale(i18n.language);

    if (period.type === 'month') {
      return start.format('MMMM YYYY');
    }

    if (period.type === 'year') {
      return start.format('YYYY');
    }

    return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
  };

  return (
    <div className="flex lg:flex-row flex-col gap-3 lg:items-center justify-between">
      <div
        role="tablist"
        className="flex bg-secondaryBlack rounded-2xl p-1 self-start"
      >
        {PERIOD_TYPES.map(({ type, labelKey }) => (
          <button
            key={type}
            role="tab"
            aria-selected={period.type === type}
            className={`px-4 py-1.5 rounded-xl text-sm transition-colors ${
              period.type === type
                ? 'bg-mainBlue text-mainBlack font-semibold'
                : 'text-textGray hover:text-mainWhite'
            }`}
            onClick={() => handleTypeChange(type)}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>

      {period.type === 'custom' ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-textGray">
          <label className="flex items-center gap-2">
            {t(COMMON_TRANSLATE_KEYS.FROM)}
            <input
              type="date"
              className="bg-secondaryBlack text-mainWhite rounded-xl px-3 py-1.5 [color-scheme:dark]"
              value={dayjs(period.start).format(INPUT_FORMAT)}
              max={dayjs(period.end).format(INPUT_FORMAT)}
              onChange={(e) => handleCustomDateChange('start', e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2">
            {t(COMMON_TRANSLATE_KEYS.UNTIL)}
            <input
              type="date"
              className="bg-secondaryBlack text-mainWhite rounded-xl px-3 py-1.5 [color-scheme:dark]"
              value={dayjs(period.end).format(INPUT_FORMAT)}
              min={dayjs(period.start).format(INPUT_FORMAT)}
              onChange={(e) => handleCustomDateChange('end', e.target.value)}
            />
          </label>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-mainWhite">
          <button
            className="p-1.5 rounded-xl hover:bg-secondaryBlack"
            aria-label={t(COMMON_TRANSLATE_KEYS.STATS_PREVIOUS)}
            onClick={() => onChange(shiftPeriod(period, -1))}
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <span className="min-w-[11rem] text-center capitalize">
            {getPeriodLabel()}
          </span>
          <button
            className="p-1.5 rounded-xl hover:bg-secondaryBlack"
            aria-label={t(COMMON_TRANSLATE_KEYS.STATS_NEXT)}
            onClick={() => onChange(shiftPeriod(period, 1))}
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default PeriodFilter;
